import { useQuery } from '@apollo/client'
import { addDays, parseISO, startOfDay } from 'date-fns'
import { useMemo } from 'react'
import {
  TIME_ENTRY_PREVIEW_ACTIVITIES_QUERY,
  TIME_ENTRY_PREVIEW_SALARY_WORK_TIME_QUERY,
} from './queries'
import type { ActivitySpan, TimeSpan } from './time-entry-pause'

/**
 * The selected user's paid pause threshold and driver activities around a
 * registration. Activities are fetched for whole days so editing the times
 * does not refetch on every change.
 */
export function useTimeEntryPreviewData(
  userId: string,
  span: TimeSpan,
  enabled: boolean
) {
  const windowStart = startOfDay(span.start).toISOString()
  const windowEnd = addDays(startOfDay(span.end), 1).toISOString()

  const { data: salaryWorkTimeData } = useQuery(
    TIME_ENTRY_PREVIEW_SALARY_WORK_TIME_QUERY,
    { variables: { userId }, skip: !enabled || !userId, errorPolicy: 'ignore' }
  )

  const { data: activitiesData } = useQuery(
    TIME_ENTRY_PREVIEW_ACTIVITIES_QUERY,
    {
      variables: { userId, dateStart: windowStart, dateEnd: windowEnd },
      skip: !enabled || !userId,
      errorPolicy: 'ignore',
    }
  )

  const activityNodes = activitiesData?.allFrontendDriverActivities
  const activities = useMemo<ActivitySpan[]>(
    () =>
      (activityNodes ?? []).map(activity => ({
        activityType: activity.activityType,
        start: parseISO(activity.datetimeStart),
        end: parseISO(activity.datetimeEnd),
      })),
    [activityNodes]
  )

  return {
    paidPauseThreshold:
      salaryWorkTimeData?.user?.salaryWorkTime?.paidPauseThreshold ?? null,
    activities,
    activitiesLoaded: !!activitiesData,
  }
}
